import { action, makeObservable, observable } from 'mobx'

import SharedStore from 'shared/stores/shared.store'

import {
  createNaturalPersonsEntityApi,
  NaturalPersonsEntityApi,
  NaturalPersonResponse,
  NaturalPersonSessionResponse,
  AddressResponse,
  PersonalReqData,
  AddressReqData,
  NaturalPersonEntityReqData,
  TaxReqData,
} from '../api/naturalPersonsEntity.api'

type EntityWithAddress = NaturalPersonResponse & AddressResponse

export class NaturalPersonsEntityStore extends SharedStore<NaturalPersonsEntityApi> {
  storeName = 'NaturalPersonsEntityStore'
  declare childApi: NaturalPersonsEntityApi

  @observable entity: EntityWithAddress = null
  @observable verificationSession: NaturalPersonSessionResponse = null

  constructor(root) {
    super()
    this.childApi = createNaturalPersonsEntityApi(root.apiClient)

    makeObservable(this)
  }

  @action setEntity = (entity: EntityWithAddress) => {
    this.entity = entity
  }

  @action setVerificationSession = (session: NaturalPersonSessionResponse) => {
    this.verificationSession = session
  }

  @action resetVerificationSession = () => {
    this.verificationSession = null
  }

  createEntity = async (data?: NaturalPersonEntityReqData) => {
    this.toggleLoading(true)

    const response = await this.childApi.createEntity(data)

    if (response.responseType === 'success') {
      this.setEntity(response.data)
    }

    this.toggleLoading(false)

    return response
  }

  fetchEntity = async (id: string) => {
    this.toggleLoading(true)

    const response = await this.childApi.fetchEntity(id)

    if (response.responseType === 'success') {
      const existingAddress = response.included?.[0]?.attributes

      this.setEntity({
        ...response.data,
        ...existingAddress,
      })
    }

    this.toggleLoading(false)

    return response
  }

  updatePersonal = async (id: string, data: PersonalReqData) => await this.childApi.updatePersonal(id, data)

  updateEntity = async (id: string, data: NaturalPersonEntityReqData) => await this.childApi.updateEntity(id, data)

  updateAddress = async (id: string, data: AddressReqData) => await this.childApi.updateAddress(id, data)

  updateTax = async (id: string, data: TaxReqData) => await this.childApi.updateTax(id, data)

  getVerificationSession = async (id: string) => {
    this.toggleLoading(true)

    const response = await this.childApi.getCurrentSession(id)

    if (response.responseType === 'success') {
      this.setVerificationSession(response.data)
    }

    this.toggleLoading(false)

    return response
  }

  createSession = async (id: string) => {
    this.toggleLoading(true)

    const response = await this.childApi.createSession(id)

    if (response.responseType === 'success') {
      this.setVerificationSession(response.data)
    }

    this.toggleLoading(false)

    return response
  }
}
